/**
 * The user-settings store — theme / locale / timezone, each auto-detected by
 * default and user-overridable, persisted to the user record so the choice
 * survives a reload and syncs across devices. Constructed ONCE in the
 * composition root (`App`), it feeds the shell's `ThemeProvider` (theme),
 * `I18nProvider` (locale), and the timezone formatter (tz) their EFFECTIVE
 * values, while the settings surface edits the OVERRIDE — the `auto` choice
 * clears an override back to auto-detection (persisted as `null`).
 *
 * Persistence goes through an injected {@link SettingsGateway} (DIP — the real
 * `ApiClient` satisfies it structurally, a fake drives the tests). A change is
 * applied optimistically for instant feedback, then reconciled from the record
 * the edge returns (the edge is authority on the stored values).
 *
 * Thin + reactive-root-free (only `createSignal`, no effects/memos) so it is
 * exercised directly in `bun test` exactly like the other product stores.
 */

import { isSupportedLocale, type Locale } from '@4f/i18n';
import { type ApiError, isErr, isValidTimeZone, type Result, type Theme } from '@4f/shared';
import { type Accessor, createSignal } from 'solid-js';
import type { MeView } from '../api/types.ts';

/**
 * The persisted-settings slice the store adopts on load — the theme/locale/tz
 * fields of the signed-in user (`AuthUserView`). A `null` locale/timezone means
 * "never overridden": the store falls back to the auto-detected default.
 */
export interface PersistedSettings {
  readonly theme: Theme;
  readonly locale: string | null;
  readonly timezone: string | null;
}

/**
 * A settings patch sent to the edge. Every field is optional; a present
 * `locale`/`timezone` of `null` clears the override back to auto-detection.
 */
export interface SettingsPatch {
  readonly theme?: Theme;
  readonly locale?: string | null;
  readonly timezone?: string | null;
}

/** The persistence port the store depends on (DIP) — `ApiClient` satisfies it. */
export interface SettingsGateway {
  updateSettings(input: SettingsPatch): Promise<Result<MeView, ApiError>>;
}

/** The auto-detected fallbacks an unset override resolves to. */
export interface SettingsDefaults {
  readonly locale: Locale;
  readonly timeZone: string;
}

export interface SettingsStore {
  /** The theme override (`auto` follows the OS) — fed to `ThemeProvider`. */
  readonly theme: Accessor<Theme>;
  /** The effective locale (override ?? auto-detected) — fed to `I18nProvider`. */
  readonly locale: Accessor<Locale>;
  /** The effective timezone (override ?? auto-detected) — fed to the tz formatter. */
  readonly timeZone: Accessor<string>;
  /** The locale override the surface edits — `null` when following auto-detection. */
  readonly localeOverride: Accessor<Locale | null>;
  /** The timezone override the surface edits — `null` when following auto-detection. */
  readonly timeZoneOverride: Accessor<string | null>;
  /** True while a persist round-trip is in flight. */
  readonly saving: Accessor<boolean>;
  /** The last persist error, or `null`. */
  readonly error: Accessor<ApiError | null>;
  /** The auto-detected timezone — the label for the "follow detection" choice. */
  readonly detectedTimeZone: string;
  /** Adopt the signed-in user's persisted settings (the read-on-load path). */
  hydrate(settings: PersistedSettings): void;
  /** Set + persist the theme override. */
  setTheme(theme: Theme): Promise<void>;
  /** Set + persist the locale override; `null` follows auto-detection. */
  setLocale(locale: Locale | null): Promise<void>;
  /** Set + persist the timezone override; `null` follows auto-detection. */
  setTimeZone(timeZone: string | null): Promise<void>;
}

/** Build the settings store around the injected gateway + auto-detected defaults. */
export function createSettingsStore(
  gateway: SettingsGateway,
  defaults: SettingsDefaults,
): SettingsStore {
  const [theme, applyTheme] = createSignal<Theme>('auto');
  const [localeOverride, applyLocaleOverride] = createSignal<Locale | null>(null);
  const [timeZoneOverride, applyTimeZoneOverride] = createSignal<string | null>(null);
  // In-flight save COUNT, not a boolean: theme/locale/tz can be changed in quick
  // succession (the settings surface fires one `PATCH /me` each), so a shared
  // boolean would clear the instant the FIRST completes — falsely signalling
  // "saved" while a sibling save is still in flight, and a reload racing that
  // window silently drops it. Counting keeps `saving()` true until ALL settle.
  const [savingCount, setSavingCount] = createSignal(0);
  const saving = (): boolean => savingCount() > 0;
  const [error, setError] = createSignal<ApiError | null>(null);
  // Per-field write sequence. Two rapid saves to the SAME field can resolve out of
  // order — an older `PATCH /me` echo arriving after a newer one would apply its
  // stale value last. Each save stamps its field's next sequence and reconciles
  // ONLY while still the latest, so the newest value (its optimistic set + its own
  // echo) always wins regardless of resolution order. Counted PER FIELD, so this
  // never couples independent theme/locale/timezone saves (an absent key isn't bumped).
  const seq = { theme: 0, locale: 0, timezone: 0 };

  const locale = (): Locale => localeOverride() ?? defaults.locale;
  const timeZone = (): string => timeZoneOverride() ?? defaults.timeZone;

  function hydrate(settings: PersistedSettings): void {
    applyTheme(settings.theme);
    // Only a SUPPORTED persisted tag becomes an override; anything else (null, a
    // dropped locale) falls back to the auto-detected default.
    applyLocaleOverride(
      settings.locale && isSupportedLocale(settings.locale) ? settings.locale : null,
    );
    // Same guard as the locale above, and for a sharper reason: an unresolvable
    // zone makes `Intl` throw inside `formatDateTime`, and this SPA has no error
    // boundary — a legacy row would crash the shell before the user could reach
    // the surface that clears it. Only a zone `Intl` resolves becomes an override.
    applyTimeZoneOverride(resolveTimeZoneOverride(settings.timezone));
  }

  async function persist(patch: SettingsPatch, optimistic: () => void): Promise<void> {
    optimistic();
    // Stamp each patched field with its next sequence (an absent key keeps the
    // current one, so it is never treated as stale).
    const mine = {
      theme: patch.theme !== undefined ? ++seq.theme : seq.theme,
      locale: patch.locale !== undefined ? ++seq.locale : seq.locale,
      timezone: patch.timezone !== undefined ? ++seq.timezone : seq.timezone,
    };
    setSavingCount((n) => n + 1);
    setError(null);
    try {
      const result = await gateway.updateSettings(patch);
      if (isErr(result)) {
        // Keep the optimistic value for this session (the surface surfaces the
        // error); a reload re-reads the still-persisted prior value.
        setError(result.error);
        return;
      }
      // Reconcile ONLY the field(s) this call patched, AND only while this call is
      // still the latest write to that field. The edge echoes the FULL user, so
      // re-hydrating all three would let a single-field save clobber a CONCURRENT
      // save's field (cross-field), and applying a superseded echo would revert a
      // newer same-field value (same-field) — the sequence guard closes both.
      const next = result.value.user;
      if (patch.theme !== undefined && mine.theme === seq.theme) {
        applyTheme(next.theme);
      }
      if (patch.locale !== undefined && mine.locale === seq.locale) {
        applyLocaleOverride(next.locale && isSupportedLocale(next.locale) ? next.locale : null);
      }
      if (patch.timezone !== undefined && mine.timezone === seq.timezone) {
        applyTimeZoneOverride(resolveTimeZoneOverride(next.timezone));
      }
    } finally {
      setSavingCount((n) => n - 1);
    }
  }

  return {
    theme,
    locale,
    timeZone,
    localeOverride,
    timeZoneOverride,
    saving,
    error,
    detectedTimeZone: defaults.timeZone,
    hydrate,
    setTheme: (next) => persist({ theme: next }, () => applyTheme(next)),
    setLocale: (next) => persist({ locale: next }, () => applyLocaleOverride(next)),
    setTimeZone: (next) =>
      persist({ timezone: next }, () => applyTimeZoneOverride(resolveTimeZoneOverride(next))),
  };
}

/**
 * A persisted/echoed timezone becomes an override only when `Intl` can resolve
 * it; anything else (absent, or a legacy row written before `PATCH /me`
 * validated the column) falls back to auto-detection. Mirrors the
 * `isSupportedLocale` guard on the sibling field.
 */
function resolveTimeZoneOverride(value: string | null | undefined): string | null {
  return typeof value === 'string' && isValidTimeZone(value) ? value : null;
}
